import { ReactElement } from "react";
import { TextLink } from "_/components/router";
import { t } from "@lingui/core/macro";

import {
  Project,
  Optimization,
  StepStatus,
  useStartOptimizationComparison,
  useProjectOptimizations,
} from "_/data/projects";
import { projectUrls } from "_/routes";
import { useAddToast } from "_/components/toasts";
import { Button } from "_/components/button";
import * as S from "./styled";
import { StepSymbol } from "./utils";

/**
 * Substep row rendered beneath the OPTIMIZE_THERMALS step when it is running or
 * completed (cooling tower strategy only).
 *
 * Manages the before/after comparison jobs that are child jobs of the
 * parent optimization. It does not render the OPTIMIZE_THERMALS step itself —
 * that is handled by the parent `OptimizationStepRow`. This component only covers
 * the "Generate before/after comparison" substep: showing its status, progress, a
 * button to kick off the jobs if not yet started, and a link to view results once
 * complete.
 */
export const OptimizationStepSection = ({
  project,
  optimization,
}: {
  project: Project;
  optimization: Optimization;
}): ReactElement => {
  const addToast = useAddToast();
  const { data: comparisons = [] } = useProjectOptimizations(project.id, {
    parentId: optimization.job.id,
  });
  const startComparison = useStartOptimizationComparison({
    projectId: project.id,
    revisionId: optimization.revisionId,
    optimizationId: optimization.job.id,
  });

  const original = comparisons.find(
    (o) => o.job.input.simulationRole === "original"
  );
  const optimized = comparisons.find(
    (o) => o.job.input.simulationRole === "optimized"
  );
  const comparisonStarted = !!original || !!optimized;

  let comparisonStatus: StepStatus;
  if (original?.job.status === "failed" || optimized?.job.status === "failed") {
    comparisonStatus = "failed";
  } else if (
    original?.job.status === "completed" &&
    (!optimized || optimized.job.status === "completed")
  ) {
    comparisonStatus = "completed";
  } else {
    comparisonStatus = "running";
  }
  const comparisonProgress =
    ((original?.job.progress ?? 0) + (optimized?.job.progress ?? 0)) /
    (optimized ? 2 : 1);

  return (
    <S.OptimizationSection>
      <S.StepItemRow>
        <S.OptimizationIconWrapper>
          <StepSymbol
            status={comparisonStarted ? comparisonStatus : "pending"}
          />
        </S.OptimizationIconWrapper>
        <S.StepItemContent>
          <S.OptimizationLabelRow>
            <S.OptimizationLabelText>
              <S.OptimizationSubstepTitle>
                {!comparisonStarted &&
                  t`components.controls.optimize.generate-visuals`}
                {comparisonStarted && comparisonStatus === "running" && (
                  <>
                    {t`components.controls.optimize.visuals-progress-subtitle-running`}
                    <S.OptimizationSubstepTitleSmall>
                      {t`components.controls.optimize.visuals-progress-subtitle-running-detail`}
                    </S.OptimizationSubstepTitleSmall>
                  </>
                )}
                {comparisonStarted &&
                  comparisonStatus === "failed" &&
                  t`components.controls.optimize.visuals-progress-subtitle-failed`}
                {comparisonStarted &&
                  comparisonStatus === "completed" &&
                  t`components.controls.optimize.visuals-progress-subtitle-completed`}
              </S.OptimizationSubstepTitle>
              {(!comparisonStarted || comparisonStatus === "running") && (
                <S.StepSubtitle $status="pending">
                  {t`components.controls.optimize.visuals-description`}
                </S.StepSubtitle>
              )}
            </S.OptimizationLabelText>
            {comparisonStarted && comparisonStatus === "running" && (
              <S.StepPercentage style={{ marginLeft: "auto" }}>
                {Math.floor(comparisonProgress * 100)}%
              </S.StepPercentage>
            )}
            {!comparisonStarted ? (
              <Button
                kind="secondary"
                size="small"
                onClick={() => {
                  startComparison.mutate(undefined, {
                    onError: () => {
                      addToast({
                        kind: "danger",
                        title: t`components.controls.optimize.start-visuals-error`,
                      });
                    },
                  });
                }}
              >
                {t`components.controls.optimize.run-visuals`}
              </Button>
            ) : comparisonStarted &&
              comparisonStatus === "running" ? null : comparisonStarted &&
              comparisonStatus === "completed" ? (
              <TextLink
                to={projectUrls.optimizeThermal(
                  project.id,
                  optimization.job.id
                )}
                variant="default"
              >
                {t`components.controls.optimize.view-results`}
              </TextLink>
            ) : null}
          </S.OptimizationLabelRow>
        </S.StepItemContent>
      </S.StepItemRow>
    </S.OptimizationSection>
  );
};
